import React, { createContext, useContext, useReducer, useEffect, useRef, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { initialParties, initialItems, initialAccounts, initialTaxes, companyInfo } from '../utils/initialData';
import { today } from '../utils/helpers';
import { isSupabaseConfigured, loadAppData, saveAppData, clearAppData } from '../supabase/dbService';
import { isEdgeMode } from '../supabase/config';
import { getTenantId } from '../tenant';
import { useAuth } from './AuthContext';

const AppContext = createContext();

// Tenant-scoped so a local cache from one company can never load under another.
const storageKey = () => `erp_app_data_${getTenantId()}`;

// Common Indian TDS sections seeded for new tenants (rates are editable in the
// TDS Master). Names are the section descriptions; rate is the % deducted.
export const DEFAULT_TDS_SECTIONS = [
  { id: 'tds-194c', section: '194C', name: 'Payment to Contractors', rate: 2 },
  { id: 'tds-194j', section: '194J', name: 'Professional / Technical Fees', rate: 10 },
  { id: 'tds-194ia', section: '194I(a)', name: 'Rent — Plant & Machinery', rate: 2 },
  { id: 'tds-194ib', section: '194I(b)', name: 'Rent — Land / Building', rate: 10 },
  { id: 'tds-194h', section: '194H', name: 'Commission / Brokerage', rate: 5 },
  { id: 'tds-194a', section: '194A', name: 'Interest (other than securities)', rate: 10 },
  { id: 'tds-194q', section: '194Q', name: 'Purchase of Goods', rate: 0.1 },
];

const TDS_PAYABLE_ACCOUNT = 'TDS Payable';

// Ensure a 'TDS Payable' liability account exists (auto-created on first TDS
// deduction). Returns a possibly-extended accounts array.
function ensureTdsPayable(accounts) {
  if (accounts.some(a => a.name === TDS_PAYABLE_ACCOUNT)) return accounts;
  return [...accounts, {
    id: uuidv4(), name: TDS_PAYABLE_ACCOUNT, group: 'Duties & Taxes',
    type: 'Other', balance: 0, openingBalance: 0, balanceType: 'Cr',
  }];
}

const TDS_RECEIVABLE_ACCOUNT = 'TDS Receivable';

// Ensure a 'TDS Receivable' asset account exists (TDS deducted by our customers,
// claimable against income tax).
function ensureTdsReceivable(accounts) {
  if (accounts.some(a => a.name === TDS_RECEIVABLE_ACCOUNT)) return accounts;
  return [...accounts, {
    id: uuidv4(), name: TDS_RECEIVABLE_ACCOUNT, group: 'Current Assets',
    type: 'Other', balance: 0, openingBalance: 0, balanceType: 'Dr',
  }];
}

// The account a TDS section posts to: its linked ledger (from the TDS Master),
// or the default TDS Payable / Receivable account.
function resolveTdsAccount(state, sectionId, defaultName) {
  const section = (state.tdsSections || []).find(s => s.id === sectionId);
  return section && section.ledgerAccount ? section.ledgerAccount : defaultName;
}

export const defaultState = {
  company: companyInfo,
  parties: initialParties,
  items: initialItems,
  accounts: initialAccounts,
  taxes: initialTaxes,
  salesInvoices: [],
  salesReturns: [],
  deliveryChallans: [],
  salesOrders: [],
  purchaseBills: [],
  purchaseReturns: [],
  purchaseOrders: [],
  receipts: [],
  payments: [],
  // Non-party (direct) vouchers: money received into / paid from a Cash/Bank
  // account against a general-ledger account, not settled against any party.
  directReceipts: [],
  directPayments: [],
  journalEntries: [],
  expenses: [],
  contraEntries: [],
  stockJournals: [],
  productions: [],
  stockIssues: [],
  financialYears: [],
  prefixSettings: [],
  accountGroups: [],
  ledgerEntries: [],
  // Bank reconciliation state, one record per bank account:
  //   { account, statementBalance, statementDate, clearedKeys: [txnKey] }
  bankReconciliations: [],
  // TDS sections (194C, 194J, …) used to deduct tax at source on expenses.
  tdsSections: DEFAULT_TDS_SECTIONS,
  counters: {
    salesInvoice: 0,
    salesReturn: 0,
    deliveryChallan: 0,
    salesOrder: 0,
    purchaseBill: 0,
    purchaseReturn: 0,
    purchaseOrder: 0,
    receipt: 0,
    payment: 0,
    directReceipt: 0,
    directPayment: 0,
    journal: 0,
    expense: 0,
    contra: 0,
    stockJournal: 0,
    production: 0,
    stockIssue: 0,
  },
};

function addLedgerEntry(state, entry, txDate) {
  return [...state.ledgerEntries, { id: uuidv4(), date: txDate || today(), ...entry }];
}

function accountNameById(state, id, fallback) {
  if (!id) return fallback;
  const acc = state.accounts.find(a => a.id === id);
  return acc ? acc.name : fallback;
}

// ── Financial-year + document-numbering helpers ──────────────────────────────
// Financial year whose date range contains the given date (or null).
function fyForDate(state, dateStr) {
  const d = dateStr || today();
  return (state.financialYears || []).find(
    fy => fy.fromDate && fy.toDate && d >= fy.fromDate && d <= fy.toDate
  ) || null;
}
// Financial-year label for tagging a transaction ('' when none defined).
function fyOf(state, dateStr) {
  return fyForDate(state, dateStr)?.name || '';
}
// Build a document number from the configured Prefix Setting for `type`
// (the one effective on/before the doc date; latest effectiveFrom wins).
// Sequence restarts each financial year, starting at the setting's Start No.
// Falls back to the legacy `PREFIX-0001` scheme when nothing is configured,
// so existing behaviour (and tests) stay identical until prefixes are set up.
function docNo(state, type, arrayKey, dateStr, fallbackPrefix, fallbackCounter) {
  const d = dateStr || today();
  const fy = fyForDate(state, d);
  let applicable = (state.prefixSettings || [])
    .filter(p => p.txnType === type && (!p.effectiveFrom || p.effectiveFrom <= d))
    .sort((a, b) => (a.effectiveFrom || '').localeCompare(b.effectiveFrom || ''));
  // Prefer a prefix whose effective date falls within the current financial
  // year (each year needs its own prefix); a blank effectiveFrom is universal.
  if (fy) {
    const inFy = applicable.filter(p => !p.effectiveFrom || (p.effectiveFrom >= fy.fromDate && p.effectiveFrom <= fy.toDate));
    if (inFy.length) applicable = inFy;
  }
  const ps = applicable[applicable.length - 1];
  if (!ps) return `${fallbackPrefix}-${String(fallbackCounter).padStart(4, '0')}`;
  const arr = state[arrayKey] || [];
  const priorInFy = fy
    ? arr.filter(x => x.date && x.date >= fy.fromDate && x.date <= fy.toDate).length
    : arr.length;
  const seq = (Number(ps.startNo) || 1) + priorInFy;
  return [ps.prefix, ps.yearCode, String(seq).padStart(4, '0')]
    .filter(v => v !== '' && v != null)
    .join('-');
}

// True when a Prefix Setting exists for this transaction type AND applies to the
// current financial year. Each financial year needs its own prefix — a prior
// year's setting no longer carries forward (a blank effectiveFrom stays
// universal). When no financial years are defined, any effective prefix counts.
// Forms require this before showing a number or allowing a save.
export function hasPrefix(state, txnType, date) {
  const d = date || today();
  const settings = (state.prefixSettings || []).filter(
    p => p.txnType === txnType && (!p.effectiveFrom || p.effectiveFrom <= d)
  );
  if (settings.length === 0) return false;
  const fy = fyForDate(state, d);
  if (!fy) return true; // no financial year configured → fall back to "any effective prefix"
  return settings.some(p => !p.effectiveFrom || (p.effectiveFrom >= fy.fromDate && p.effectiveFrom <= fy.toDate));
}

// Preview the next document number for a transaction type at a given date —
// used by forms to show a live, date-aware number (so a last-year date shows
// that year's configured prefix). Returns '' when no Prefix Setting applies,
// so the form hides the number and blocks the save.
export function previewDocNo(state, txnType, arrayKey, date, fallbackPrefix) {
  if (!hasPrefix(state, txnType, date)) return '';
  const counter = (Number(state.counters?.[txnType]) || 0) + 1;
  return docNo(state, txnType, arrayKey, date, fallbackPrefix, counter);
}

// Build a balanced set of double-entry ledger postings for an item-based document.
// kind: 'sales' | 'purchase' | 'salesReturn' | 'purchaseReturn'
//  - Party account ........ full bill amount (grand total)
//  - Basic/expense ledger .. per Item Master "Basic Account Ledger" (taxable amount)
//  - Tax ledgers ........... SGST/CGST/IGST ledgers from Tax Master (tax amount)
// All legs share the same refNo so a single delete/edit removes the whole voucher.
export function buildDocumentPostings(state, { kind, refNo, partyId, date, items = [], grandTotal = 0, isInterState = false, additionalCharges = 0, narration }) {
  const isSalesSide = kind === 'sales' || kind === 'salesReturn';
  // Party leg is a debit for normal sales and for purchase returns; a credit otherwise.
  const partyDebit = (kind === 'sales' || kind === 'purchaseReturn');
  const typeLabel = kind === 'sales' ? 'Sales' : kind === 'purchase' ? 'Purchase'
    : kind === 'salesReturn' ? 'Sales Return' : 'Purchase Return';

  // Aggregate taxable base by item basic ledger, and tax by resolved tax ledger.
  const baseMap = {};
  const taxMap = {};
  items.forEach(li => {
    const item = state.items.find(i => i.id === li.itemId);
    const baseAcct = accountNameById(state, item?.basicLedger, isSalesSide ? 'Sales' : 'Purchase');
    baseMap[baseAcct] = (baseMap[baseAcct] || 0) + (Number(li.taxableAmount) || 0);
    const taxAmt = Number(li.taxAmount) || 0;
    if (taxAmt > 0) {
      const taxRec = state.taxes.find(t => Number(t.rate) === Number(li.taxPercent));
      if (isInterState) {
        const acct = accountNameById(state, taxRec?.igstLedger, isSalesSide ? 'IGST Output' : 'IGST Input');
        taxMap[acct] = (taxMap[acct] || 0) + taxAmt;
      } else {
        const half = taxAmt / 2;
        const cg = accountNameById(state, taxRec?.cgstLedger, isSalesSide ? 'CGST Output' : 'CGST Input');
        const sg = accountNameById(state, taxRec?.sgstLedger, isSalesSide ? 'SGST Output' : 'SGST Input');
        taxMap[cg] = (taxMap[cg] || 0) + half;
        taxMap[sg] = (taxMap[sg] || 0) + half;
      }
    }
  });

  const party = state.parties.find(p => p.id === partyId);
  const partyAcct = accountNameById(state, party?.linkedLedger, isSalesSide ? 'Sundry Debtors' : 'Sundry Creditors');

  const gt = Number(grandTotal) || 0;
  const baseTotal = Object.values(baseMap).reduce((s, v) => s + v, 0);
  const taxTotal = Object.values(taxMap).reduce((s, v) => s + v, 0);
  const charges = Number(additionalCharges) || 0;
  const roundOff = +(gt - (baseTotal + taxTotal + charges)).toFixed(2);

  const mk = (account, amount, debit, pid) => ({
    id: uuidv4(),
    date: date || today(),
    type: typeLabel,
    refNo,
    partyId: pid,
    account,
    debit: debit ? +Number(amount).toFixed(2) : 0,
    credit: debit ? 0 : +Number(amount).toFixed(2),
    amount: +Number(amount).toFixed(2),
    narration: narration || `${typeLabel} ${refNo}`,
  });

  const lines = [];
  // Party account — full bill amount
  lines.push(mk(partyAcct, gt, partyDebit, partyId));
  // Basic / income-expense ledgers — opposite side of the party leg
  Object.entries(baseMap).forEach(([acct, amt]) => { if (Math.abs(amt) >= 0.005) lines.push(mk(acct, amt, !partyDebit)); });
  // Tax ledgers — same side as the basic ledgers
  Object.entries(taxMap).forEach(([acct, amt]) => { if (Math.abs(amt) >= 0.005) lines.push(mk(acct, amt, !partyDebit)); });
  // Additional charges (purchase) — same side as basic ledgers
  if (Math.abs(charges) >= 0.005) lines.push(mk('Additional Charges', charges, !partyDebit));
  // Round-off balancing leg so the voucher always balances
  if (Math.abs(roundOff) >= 0.01) lines.push(mk('Round Off', Math.abs(roundOff), roundOff > 0 ? !partyDebit : partyDebit));

  return lines;
}

function updateAccountBalance(accounts, accountName, amount) {
  return accounts.map(acc =>
    acc.name === accountName ? { ...acc, balance: Number(acc.balance) + Number(amount) } : acc
  );
}

function updateItemStock(items, itemId, qtyChange) {
  return items.map(item =>
    item.id === itemId ? { ...item, currentStock: (Number(item.currentStock) || 0) + (Number(qtyChange) || 0) } : item
  );
}

function resolvePaymentAccount(accounts, paymentMode) {
  if (paymentMode === 'Cash') return 'Cash';
  if (paymentMode === 'Bank') {
    const bank = accounts.find(a => a.type === 'Bank');
    return bank?.name || 'HDFC Bank - Current';
  }
  return null;
}

export function reducer(state, action) {
  const { type, payload } = action;
  switch (type) {
    case 'LOAD_STATE': {
      // Migrate accounts: backfill group/subGroup for accounts that don't have them
      const migratedAccounts = (payload.accounts || defaultState.accounts).map(acc => {
        if (acc.group) return acc;
        // Auto-assign group/subGroup based on account type
        const typeGroupMap = {
          Cash: { group: 'Asset', subGroup: 'Cash & Cash Equivalents' },
          Bank: { group: 'Asset', subGroup: 'Bank Accounts' },
          Income: { group: 'Revenue', subGroup: 'Direct Income' },
          Expense: { group: 'Expenditure', subGroup: 'Indirect Expenses' },
          Receivable: { group: 'Asset', subGroup: 'Sundry Debtors' },
          Payable: { group: 'Liability', subGroup: 'Sundry Creditors' },
        };
        const mapping = typeGroupMap[acc.type] || { group: 'Asset', subGroup: '' };
        return { ...acc, ...mapping };
      });
      return {
        ...defaultState,
        ...payload,
        accounts: migratedAccounts,
        company: { ...companyInfo, ...(payload.company || {}) },
        counters: { ...defaultState.counters, ...(payload.counters || {}) },
      };
    }

    case 'RESET_DATA':
      return defaultState;

    case 'UPDATE_COMPANY':
      return { ...state, company: { ...state.company, ...payload } };

    case 'ADD_PARTY':
      return { ...state, parties: [...state.parties, { id: uuidv4(), ...payload }] };
    case 'UPDATE_PARTY':
      return { ...state, parties: state.parties.map(p => p.id === payload.id ? payload : p) };
    case 'DELETE_PARTY':
      return { ...state, parties: state.parties.filter(p => p.id !== payload) };

    case 'ADD_ITEM':
      return { ...state, items: [...state.items, { id: uuidv4(), currentStock: payload.openingStock || 0, ...payload }] };
    case 'UPDATE_ITEM':
      return { ...state, items: state.items.map(i => i.id === payload.id ? payload : i) };
    case 'DELETE_ITEM':
      return { ...state, items: state.items.filter(i => i.id !== payload) };

    case 'ADD_ACCOUNT':
      return { ...state, accounts: [...state.accounts, { id: uuidv4(), balance: 0, ...payload }] };
    case 'UPDATE_ACCOUNT':
      return { ...state, accounts: state.accounts.map(a => a.id === payload.id ? payload : a) };
    case 'DELETE_ACCOUNT':
      return { ...state, accounts: state.accounts.filter(a => a.id !== payload) };

    case 'ADD_TAX':
      return { ...state, taxes: [...state.taxes, { id: uuidv4(), ...payload }] };
    case 'UPDATE_TAX':
      return { ...state, taxes: state.taxes.map(t => t.id === payload.id ? payload : t) };
    case 'DELETE_TAX':
      return { ...state, taxes: state.taxes.filter(t => t.id !== payload) };

    case 'ADD_SALES_INVOICE': {
      const counter = state.counters.salesInvoice + 1;
      const invoice = { id: uuidv4(), invoiceNo: docNo(state, 'salesInvoice', 'salesInvoices', payload.date, 'INV', counter), financialYear: fyOf(state, payload.date), status: 'Unpaid', paidAmount: 0, ...payload };
      let items = [...state.items];
      invoice.items.forEach(li => {
        items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0));
      });
      let ledger = [...state.ledgerEntries, ...buildDocumentPostings(state, {
        kind: 'sales',
        refNo: invoice.invoiceNo,
        partyId: invoice.partyId,
        date: invoice.date,
        items: invoice.items,
        grandTotal: invoice.grandTotal,
        isInterState: invoice.isInterState,
        narration: `Sales Invoice ${invoice.invoiceNo}`,
      })];
      // TDS receivable: the customer will deduct TDS on our income. Book it as an
      // immediate partial settlement (asset), leaving only the net collectible.
      let accounts = state.accounts;
      const sTds = Number(invoice.tdsAmount) || 0;
      const sTdsAcc = resolveTdsAccount(state, invoice.tdsSectionId, TDS_RECEIVABLE_ACCOUNT);
      if (sTds > 0) {
        if (sTdsAcc === TDS_RECEIVABLE_ACCOUNT) accounts = ensureTdsReceivable(accounts);
        accounts = updateAccountBalance(accounts, sTdsAcc, sTds); // Dr asset
        invoice.paidAmount = sTds;
        invoice.status = sTds >= invoice.grandTotal ? 'Paid' : 'Partial';
        invoice.tdsAccount = sTdsAcc;
        ledger = addLedgerEntry({ ...state, ledgerEntries: ledger }, {
          type: 'TDS', refNo: invoice.invoiceNo, partyId: invoice.partyId,
          account: sTdsAcc, debit: sTds, credit: 0, amount: sTds,
          narration: `TDS ${invoice.tdsSection || ''} receivable on ${invoice.invoiceNo}`.trim(),
        }, invoice.date);
      }
      return {
        ...state,
        accounts,
        items,
        salesInvoices: [...state.salesInvoices, invoice],
        ledgerEntries: ledger,
        counters: { ...state.counters, salesInvoice: counter },
      };
    }

    case 'EDIT_SALES_INVOICE': {
      const { id, data } = payload;
      const old = state.salesInvoices.find(i => i.id === id);
      if (!old) return state;
      let items = [...state.items];
      let ledger = state.ledgerEntries.filter(l => l.refNo !== old.invoiceNo);
      old.items.forEach(li => { items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      const updated = { ...old, ...data, id };
      updated.status = (old.paidAmount || 0) >= updated.grandTotal ? 'Paid' : (old.paidAmount || 0) > 0 ? 'Partial' : 'Unpaid';
      updated.paidAmount = old.paidAmount || 0;
      updated.items.forEach(li => { items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      ledger = [...ledger, ...buildDocumentPostings(state, {
        kind: 'sales',
        refNo: updated.invoiceNo,
        partyId: updated.partyId,
        date: updated.date,
        items: updated.items,
        grandTotal: updated.grandTotal,
        isInterState: updated.isInterState,
        narration: `Sales Invoice ${updated.invoiceNo}`,
      })];
      return { ...state, items, salesInvoices: state.salesInvoices.map(i => i.id === id ? updated : i), ledgerEntries: ledger };
    }

    case 'DELETE_SALES_INVOICE': {
      const inv = state.salesInvoices.find(i => i.id === payload);
      if (!inv) return state;
      let items = [...state.items];
      let deliveryChallans = state.deliveryChallans;
      if (inv.fromChallan) {
        // Invoice was converted from a challan: the challan owns the stock movement.
        // Restore the challan to Pending instead of returning stock (avoids double-count & orphan ref).
        deliveryChallans = state.deliveryChallans.map(c =>
          c.challanNo === inv.fromChallan ? { ...c, status: 'Pending', convertedTo: undefined } : c
        );
      } else {
        inv.items.forEach(li => { items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      }
      let dsAccounts = state.accounts;
      const dsTds = Number(inv.tdsAmount) || 0;
      if (dsTds > 0) dsAccounts = updateAccountBalance(dsAccounts, inv.tdsAccount || TDS_RECEIVABLE_ACCOUNT, -dsTds); // reverse Dr
      return {
        ...state,
        accounts: dsAccounts,
        items,
        deliveryChallans,
        salesInvoices: state.salesInvoices.filter(i => i.id !== payload),
        ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== inv.invoiceNo),
      };
    }

    case 'UPDATE_SALES_INVOICE': {
      const original = state.salesInvoices.find(i => i.id === payload.id);
      if (!original) return state;
      let items = [...state.items];
      original.items?.forEach(li => {
        items = updateItemStock(items, li.itemId, Number(li.qty) || 0);
      });
      (payload.items || []).forEach(li => {
        items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0));
      });
      return { ...state, items, salesInvoices: state.salesInvoices.map(i => i.id === payload.id ? { ...i, ...payload } : i) };
    }

    case 'ADD_SALES_RETURN': {
      const counter = state.counters.salesReturn + 1;
      const ret = { id: uuidv4(), returnNo: docNo(state, 'salesReturn', 'salesReturns', payload.date, 'SR', counter), financialYear: fyOf(state, payload.date), ...payload };
      let items = [...state.items];
      ret.items.forEach(li => {
        items = updateItemStock(items, li.itemId, Number(li.qty) || 0);
      });
      const ledger = [...state.ledgerEntries, ...buildDocumentPostings(state, {
        kind: 'salesReturn',
        refNo: ret.returnNo,
        partyId: ret.partyId,
        date: ret.date,
        items: ret.items,
        grandTotal: ret.grandTotal,
        isInterState: ret.isInterState,
        narration: `Sales Return ${ret.returnNo} against ${ret.againstInvoice}`,
      })];
      return {
        ...state,
        items,
        salesReturns: [...state.salesReturns, ret],
        ledgerEntries: ledger,
        counters: { ...state.counters, salesReturn: counter },
      };
    }

    case 'DELETE_SALES_RETURN': {
      const ret = state.salesReturns.find(r => r.id === payload);
      if (!ret) return state;
      let items = [...state.items];
      ret.items.forEach(li => { items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      return { ...state, items, salesReturns: state.salesReturns.filter(r => r.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== ret.returnNo) };
    }

    case 'ADD_DELIVERY_CHALLAN': {
      const counter = state.counters.deliveryChallan + 1;
      const challan = { id: uuidv4(), challanNo: docNo(state, 'deliveryChallan', 'deliveryChallans', payload.date, 'DC', counter), financialYear: fyOf(state, payload.date), status: 'Pending', ...payload };
      let items = [...state.items];
      challan.items.forEach(li => {
        items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0));
      });
      return {
        ...state,
        items,
        deliveryChallans: [...state.deliveryChallans, challan],
        counters: { ...state.counters, deliveryChallan: counter },
      };
    }

    case 'DELETE_DELIVERY_CHALLAN': {
      const ch = state.deliveryChallans.find(c => c.id === payload);
      if (!ch) return state;
      let items = [...state.items];
      ch.items.forEach(li => { items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      return { ...state, items, deliveryChallans: state.deliveryChallans.filter(c => c.id !== payload) };
    }

    case 'CONVERT_CHALLAN_TO_INVOICE': {
      const challan = state.deliveryChallans.find(c => c.id === payload);
      if (!challan) return state;
      const counter = state.counters.salesInvoice + 1;
      const invoice = {
        id: uuidv4(),
        invoiceNo: docNo(state, 'salesInvoice', 'salesInvoices', payload.date, 'INV', counter),
        financialYear: fyOf(state, payload.date),
        date: today(),
        partyId: challan.partyId,
        items: challan.items,
        paymentMode: 'Credit',
        status: 'Unpaid',
        narration: `Converted from ${challan.challanNo}`,
        ...challan.summary,
        fromChallan: challan.challanNo,
      };
      const ledger = [...state.ledgerEntries, ...buildDocumentPostings(state, {
        kind: 'sales',
        refNo: invoice.invoiceNo,
        partyId: invoice.partyId,
        date: invoice.date,
        items: invoice.items,
        grandTotal: invoice.grandTotal,
        isInterState: invoice.isInterState ?? challan.isInterState,
        narration: `Sales Invoice ${invoice.invoiceNo} from ${challan.challanNo}`,
      })];
      return {
        ...state,
        salesInvoices: [...state.salesInvoices, invoice],
        deliveryChallans: state.deliveryChallans.map(c => c.id === payload ? { ...c, status: 'Converted', convertedTo: invoice.invoiceNo } : c),
        ledgerEntries: ledger,
        counters: { ...state.counters, salesInvoice: counter },
      };
    }

    case 'ADD_SALES_ORDER': {
      const counter = state.counters.salesOrder + 1;
      return {
        ...state,
        salesOrders: [...state.salesOrders, { id: uuidv4(), orderNo: docNo(state, 'salesOrder', 'salesOrders', payload.date, 'SO', counter), financialYear: fyOf(state, payload.date), status: 'Pending', ...payload }],
        counters: { ...state.counters, salesOrder: counter },
      };
    }
    case 'UPDATE_SALES_ORDER':
      return { ...state, salesOrders: state.salesOrders.map(o => o.id === payload.id ? { ...o, ...payload } : o) };

    case 'DELETE_SALES_ORDER':
      return { ...state, salesOrders: state.salesOrders.filter(o => o.id !== payload) };

    case 'ADD_PURCHASE_BILL': {
      const counter = state.counters.purchaseBill + 1;
      const bill = { id: uuidv4(), entryNo: docNo(state, 'purchaseBill', 'purchaseBills', payload.date, 'PB', counter), financialYear: fyOf(state, payload.date), status: 'Unpaid', paidAmount: 0, ...payload };
      let items = [...state.items];
      bill.items.forEach(li => {
        items = updateItemStock(items, li.itemId, Number(li.qty) || 0);
      });
      let ledger = [...state.ledgerEntries, ...buildDocumentPostings(state, {
        kind: 'purchase',
        refNo: bill.entryNo,
        partyId: bill.partyId,
        date: bill.date,
        items: bill.items,
        grandTotal: bill.grandTotal,
        isInterState: bill.isInterState,
        additionalCharges: bill.additionalCharges,
        narration: `Purchase Bill ${bill.entryNo}`,
      })];
      // TDS on purchase: we deduct TDS when paying the supplier. Book it as an
      // immediate partial settlement (TDS Payable), leaving only the net to pay.
      let accounts = state.accounts;
      const pTds = Number(bill.tdsAmount) || 0;
      const pTdsAcc = resolveTdsAccount(state, bill.tdsSectionId, TDS_PAYABLE_ACCOUNT);
      if (pTds > 0) {
        if (pTdsAcc === TDS_PAYABLE_ACCOUNT) accounts = ensureTdsPayable(accounts);
        accounts = updateAccountBalance(accounts, pTdsAcc, -pTds); // Cr liability
        bill.paidAmount = pTds;
        bill.status = pTds >= bill.grandTotal ? 'Paid' : 'Partial';
        bill.tdsAccount = pTdsAcc;
        ledger = addLedgerEntry({ ...state, ledgerEntries: ledger }, {
          type: 'TDS', refNo: bill.entryNo, partyId: bill.partyId,
          account: pTdsAcc, debit: 0, credit: pTds, amount: pTds,
          narration: `TDS ${bill.tdsSection || ''} on ${bill.entryNo}`.trim(),
        }, bill.date);
      }
      return {
        ...state,
        accounts,
        items,
        purchaseBills: [...state.purchaseBills, bill],
        ledgerEntries: ledger,
        counters: { ...state.counters, purchaseBill: counter },
      };
    }

    case 'DELETE_PURCHASE_BILL': {
      const bill = state.purchaseBills.find(b => b.id === payload);
      if (!bill) return state;
      let items = [...state.items];
      bill.items.forEach(li => { items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      let accounts = state.accounts;
      const pTds = Number(bill.tdsAmount) || 0;
      if (pTds > 0) accounts = updateAccountBalance(accounts, bill.tdsAccount || TDS_PAYABLE_ACCOUNT, pTds); // reverse Cr
      return { ...state, accounts, items, purchaseBills: state.purchaseBills.filter(b => b.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== bill.entryNo) };
    }

    case 'ADD_PURCHASE_RETURN': {
      const counter = state.counters.purchaseReturn + 1;
      const ret = { id: uuidv4(), returnNo: docNo(state, 'purchaseReturn', 'purchaseReturns', payload.date, 'PR', counter), financialYear: fyOf(state, payload.date), ...payload };
      let items = [...state.items];
      ret.items.forEach(li => {
        items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0));
      });
      const ledger = [...state.ledgerEntries, ...buildDocumentPostings(state, {
        kind: 'purchaseReturn',
        refNo: ret.returnNo,
        partyId: ret.partyId,
        date: ret.date,
        items: ret.items,
        grandTotal: ret.grandTotal,
        isInterState: ret.isInterState,
        narration: `Purchase Return ${ret.returnNo}`,
      })];
      return {
        ...state,
        items,
        purchaseReturns: [...state.purchaseReturns, ret],
        ledgerEntries: ledger,
        counters: { ...state.counters, purchaseReturn: counter },
      };
    }

    case 'DELETE_PURCHASE_RETURN': {
      const ret = state.purchaseReturns.find(r => r.id === payload);
      if (!ret) return state;
      let items = [...state.items];
      ret.items.forEach(li => { items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      return { ...state, items, purchaseReturns: state.purchaseReturns.filter(r => r.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== ret.returnNo) };
    }

    case 'ADD_PURCHASE_ORDER': {
      const counter = state.counters.purchaseOrder + 1;
      return {
        ...state,
        purchaseOrders: [...state.purchaseOrders, { id: uuidv4(), orderNo: docNo(state, 'purchaseOrder', 'purchaseOrders', payload.date, 'PO', counter), financialYear: fyOf(state, payload.date), status: 'Pending', ...payload }],
        counters: { ...state.counters, purchaseOrder: counter },
      };
    }

    case 'DELETE_PURCHASE_ORDER':
      return { ...state, purchaseOrders: state.purchaseOrders.filter(o => o.id !== payload) };

    case 'ADD_RECEIPT': {
      const counter = state.counters.receipt + 1;
      const receipt = { id: uuidv4(), receiptNo: docNo(state, 'receipt', 'receipts', payload.date, 'RCT', counter), financialYear: fyOf(state, payload.date), ...payload };
      const receiptAccountName = payload.mode === 'Cash'
        ? 'Cash'
        : (payload.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'Cash');
      let accounts = updateAccountBalance(state.accounts, receiptAccountName, receipt.amount);
      let invoices = state.salesInvoices;
      // Multi-invoice allocation: apply each allocated amount to its invoice.
      // Falls back to the legacy single-invoice (full amount) behaviour.
      const rcptAllocs = (receipt.allocations && receipt.allocations.length)
        ? receipt.allocations
        : (receipt.againstInvoice ? [{ invoiceNo: receipt.againstInvoice, amount: receipt.amount }] : []);
      if (rcptAllocs.length) {
        invoices = invoices.map(inv => {
          const a = rcptAllocs.find(x => x.invoiceNo === inv.invoiceNo);
          if (a) {
            const paid = (inv.paidAmount || 0) + (Number(a.amount) || 0);
            const status = paid >= inv.grandTotal ? 'Paid' : paid > 0 ? 'Partial' : 'Unpaid';
            return { ...inv, paidAmount: paid, status };
          }
          return inv;
        });
      }
      let ledger = addLedgerEntry(state, {
        type: 'Receipt',
        refNo: receipt.receiptNo,
        partyId: receipt.partyId,
        debit: 0,
        credit: receipt.amount,
        account: receiptAccountName,
        amount: receipt.amount,
        narration: receipt.narration || `Receipt ${receipt.receiptNo}`,
      }, receipt.date);
      return {
        ...state,
        accounts,
        salesInvoices: invoices,
        receipts: [...state.receipts, receipt],
        ledgerEntries: ledger,
        counters: { ...state.counters, receipt: counter },
      };
    }

    case 'DELETE_RECEIPT': {
      const rct = state.receipts.find(r => r.id === payload);
      if (!rct) return state;
      const rctAccountName = rct.mode === 'Cash'
        ? 'Cash'
        : (rct.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'HDFC Bank - Current');
      let accounts = updateAccountBalance(state.accounts, rctAccountName, -rct.amount);
      let invoices = state.salesInvoices;
      const delAllocs = (rct.allocations && rct.allocations.length)
        ? rct.allocations
        : (rct.againstInvoice ? [{ invoiceNo: rct.againstInvoice, amount: rct.amount }] : []);
      if (delAllocs.length) {
        invoices = invoices.map(inv => {
          const a = delAllocs.find(x => x.invoiceNo === inv.invoiceNo);
          if (a) {
            const paid = Math.max(0, (inv.paidAmount || 0) - (Number(a.amount) || 0));
            return { ...inv, paidAmount: paid, status: paid <= 0 ? 'Unpaid' : paid >= inv.grandTotal ? 'Paid' : 'Partial' };
          }
          return inv;
        });
      }
      return { ...state, accounts, salesInvoices: invoices, receipts: state.receipts.filter(r => r.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== rct.receiptNo) };
    }

    case 'ADD_PAYMENT': {
      const counter = state.counters.payment + 1;
      const payment = { id: uuidv4(), paymentNo: docNo(state, 'payment', 'payments', payload.date, 'PAY', counter), financialYear: fyOf(state, payload.date), ...payload };
      const paymentAccountName = payload.mode === 'Cash'
        ? 'Cash'
        : (payload.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'Cash');
      let accounts = updateAccountBalance(state.accounts, paymentAccountName, -payment.amount);
      let bills = state.purchaseBills;
      // Multi-bill allocation: apply each allocated amount to its bill.
      // Falls back to the legacy single-bill (full amount) behaviour.
      const payAllocs = (payment.allocations && payment.allocations.length)
        ? payment.allocations
        : (payment.againstBill ? [{ entryNo: payment.againstBill, amount: payment.amount }] : []);
      if (payAllocs.length) {
        bills = bills.map(bill => {
          const a = payAllocs.find(x => x.entryNo === bill.entryNo);
          if (a) {
            const paid = (bill.paidAmount || 0) + (Number(a.amount) || 0);
            const status = paid >= bill.grandTotal ? 'Paid' : paid > 0 ? 'Partial' : 'Unpaid';
            return { ...bill, paidAmount: paid, status };
          }
          return bill;
        });
      }
      let ledger = addLedgerEntry(state, {
        type: 'Payment',
        refNo: payment.paymentNo,
        partyId: payment.partyId,
        debit: payment.amount,
        credit: 0,
        account: paymentAccountName,
        amount: payment.amount,
        narration: payment.narration || `Payment ${payment.paymentNo}`,
      }, payment.date);
      return {
        ...state,
        accounts,
        purchaseBills: bills,
        payments: [...state.payments, payment],
        ledgerEntries: ledger,
        counters: { ...state.counters, payment: counter },
      };
    }

    case 'DELETE_PAYMENT': {
      const pay = state.payments.find(p => p.id === payload);
      if (!pay) return state;
      const payAccountName = pay.mode === 'Cash'
        ? 'Cash'
        : (pay.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'HDFC Bank - Current');
      let accounts = updateAccountBalance(state.accounts, payAccountName, pay.amount);
      let bills = state.purchaseBills;
      const delPayAllocs = (pay.allocations && pay.allocations.length)
        ? pay.allocations
        : (pay.againstBill ? [{ entryNo: pay.againstBill, amount: pay.amount }] : []);
      if (delPayAllocs.length) {
        bills = bills.map(bill => {
          const a = delPayAllocs.find(x => x.entryNo === bill.entryNo);
          if (a) {
            const paid = Math.max(0, (bill.paidAmount || 0) - (Number(a.amount) || 0));
            return { ...bill, paidAmount: paid, status: paid <= 0 ? 'Unpaid' : paid >= bill.grandTotal ? 'Paid' : 'Partial' };
          }
          return bill;
        });
      }
      return { ...state, accounts, purchaseBills: bills, payments: state.payments.filter(p => p.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== pay.paymentNo) };
    }

    // ── Direct (non-party) Receipt: Dr Cash/Bank, Cr the chosen ledger account.
    //    e.g. capital introduced, loan received, interest income, refund.
    case 'ADD_DIRECT_RECEIPT': {
      const counter = state.counters.directReceipt + 1;
      const receipt = { id: uuidv4(), voucherNo: docNo(state, 'directReceipt', 'directReceipts', payload.date, 'DRV', counter), financialYear: fyOf(state, payload.date), ...payload };
      const cashBankName = payload.mode === 'Cash'
        ? 'Cash'
        : (payload.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'Cash');
      let accounts = updateAccountBalance(state.accounts, cashBankName, receipt.amount);   // Dr asset
      accounts = updateAccountBalance(accounts, receipt.ledgerAccount, -receipt.amount);    // Cr ledger
      const ledger = [
        ...state.ledgerEntries,
        { id: uuidv4(), date: receipt.date, type: 'Direct Receipt', refNo: receipt.voucherNo, account: cashBankName, debit: receipt.amount, credit: 0, amount: receipt.amount, narration: receipt.narration || `Received in ${cashBankName}` },
        { id: uuidv4(), date: receipt.date, type: 'Direct Receipt', refNo: receipt.voucherNo, account: receipt.ledgerAccount, debit: 0, credit: receipt.amount, amount: receipt.amount, narration: receipt.narration || `Being amount received` },
      ];
      return { ...state, accounts, directReceipts: [...state.directReceipts, receipt], ledgerEntries: ledger, counters: { ...state.counters, directReceipt: counter } };
    }

    case 'DELETE_DIRECT_RECEIPT': {
      const rc = state.directReceipts.find(r => r.id === payload);
      if (!rc) return state;
      const cashBankName = rc.mode === 'Cash' ? 'Cash' : (rc.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'Cash');
      let accounts = updateAccountBalance(state.accounts, cashBankName, -rc.amount);
      accounts = updateAccountBalance(accounts, rc.ledgerAccount, rc.amount);
      return { ...state, accounts, directReceipts: state.directReceipts.filter(r => r.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== rc.voucherNo) };
    }

    // ── Direct (non-party) Payment: Dr the chosen ledger account, Cr Cash/Bank.
    //    e.g. bank charges, direct expenses, drawings, loan repayment.
    case 'ADD_DIRECT_PAYMENT': {
      const counter = state.counters.directPayment + 1;
      const payment = { id: uuidv4(), voucherNo: docNo(state, 'directPayment', 'directPayments', payload.date, 'DPV', counter), financialYear: fyOf(state, payload.date), ...payload };
      const cashBankName = payload.mode === 'Cash'
        ? 'Cash'
        : (payload.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'Cash');
      let accounts = updateAccountBalance(state.accounts, cashBankName, -payment.amount);  // Cr asset
      accounts = updateAccountBalance(accounts, payment.ledgerAccount, payment.amount);    // Dr ledger
      const ledger = [
        ...state.ledgerEntries,
        { id: uuidv4(), date: payment.date, type: 'Direct Payment', refNo: payment.voucherNo, account: payment.ledgerAccount, debit: payment.amount, credit: 0, amount: payment.amount, narration: payment.narration || `Being amount paid` },
        { id: uuidv4(), date: payment.date, type: 'Direct Payment', refNo: payment.voucherNo, account: cashBankName, debit: 0, credit: payment.amount, amount: payment.amount, narration: payment.narration || `Paid from ${cashBankName}` },
      ];
      return { ...state, accounts, directPayments: [...state.directPayments, payment], ledgerEntries: ledger, counters: { ...state.counters, directPayment: counter } };
    }

    case 'DELETE_DIRECT_PAYMENT': {
      const pm = state.directPayments.find(p => p.id === payload);
      if (!pm) return state;
      const cashBankName = pm.mode === 'Cash' ? 'Cash' : (pm.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'Cash');
      let accounts = updateAccountBalance(state.accounts, cashBankName, pm.amount);
      accounts = updateAccountBalance(accounts, pm.ledgerAccount, -pm.amount);
      return { ...state, accounts, directPayments: state.directPayments.filter(p => p.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== pm.voucherNo) };
    }

    case 'UPSERT_BANK_RECON': {
      // payload: { account, statementBalance, statementDate, clearedKeys }
      const rest = (state.bankReconciliations || []).filter(r => r.account !== payload.account);
      return { ...state, bankReconciliations: [...rest, { ...payload }] };
    }

    case 'ADD_TDS_SECTION':
      return { ...state, tdsSections: [...(state.tdsSections || []), { id: uuidv4(), ...payload }] };
    case 'UPDATE_TDS_SECTION':
      return { ...state, tdsSections: (state.tdsSections || []).map(t => t.id === payload.id ? { ...t, ...payload } : t) };
    case 'DELETE_TDS_SECTION':
      return { ...state, tdsSections: (state.tdsSections || []).filter(t => t.id !== payload) };

    case 'ADD_JOURNAL_ENTRY': {
      const counter = state.counters.journal + 1;
      const entry = { id: uuidv4(), entryNo: docNo(state, 'journal', 'journalEntries', payload.date, 'JV', counter), financialYear: fyOf(state, payload.date), ...payload };
      return {
        ...state,
        journalEntries: [...state.journalEntries, entry],
        ledgerEntries: [...state.ledgerEntries, ...entry.lines.map(line => ({
          id: uuidv4(),
          date: entry.date,
          type: 'Journal',
          refNo: entry.entryNo,
          account: line.account,
          debit: line.debit || 0,
          credit: line.credit || 0,
          amount: line.debit || line.credit || 0,
          narration: entry.narration,
        }))],
        counters: { ...state.counters, journal: counter },
      };
    }

    case 'DELETE_JOURNAL_ENTRY': {
      const jv = state.journalEntries.find(j => j.id === payload);
      if (!jv) return state;
      return { ...state, journalEntries: state.journalEntries.filter(j => j.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== jv.entryNo) };
    }

    case 'ADD_EXPENSE': {
      const counter = state.counters.expense + 1;
      const expense = { id: uuidv4(), expenseNo: docNo(state, 'expense', 'expenses', payload.date, 'EXP', counter), financialYear: fyOf(state, payload.date), ...payload };
      const expenseAccountName = expense.paidFrom === 'Cash'
        ? 'Cash'
        : (expense.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'Cash');
      // Expense is booked at GROSS; only the NET (gross − TDS) leaves the bank/
      // cash, and the deducted TDS becomes a payable to the government.
      const tds = Number(expense.tdsAmount) || 0;
      const net = expense.amount - tds;
      const expTdsAcc = resolveTdsAccount(state, expense.tdsSectionId, TDS_PAYABLE_ACCOUNT);
      let accounts = state.accounts;
      if (tds > 0 && expTdsAcc === TDS_PAYABLE_ACCOUNT) accounts = ensureTdsPayable(accounts);
      accounts = updateAccountBalance(accounts, expenseAccountName, -net);
      if (tds > 0) { accounts = updateAccountBalance(accounts, expTdsAcc, -tds); expense.tdsAccount = expTdsAcc; } // Cr → more negative
      let ledger = addLedgerEntry(state, {
        type: 'Expense',
        refNo: expense.expenseNo,
        account: expense.expenseHead,
        debit: expense.amount,
        credit: 0,
        amount: expense.amount,
        narration: expense.narration || `${expense.expenseHead} expense`,
      }, expense.date);
      if (tds > 0) {
        ledger = addLedgerEntry({ ...state, ledgerEntries: ledger }, {
          type: 'TDS',
          refNo: expense.expenseNo,
          account: expTdsAcc,
          debit: 0,
          credit: tds,
          amount: tds,
          narration: `TDS ${expense.tdsSection || ''} on ${expense.expenseHead}`.trim(),
        }, expense.date);
      }
      return {
        ...state,
        accounts,
        expenses: [...state.expenses, expense],
        ledgerEntries: ledger,
        counters: { ...state.counters, expense: counter },
      };
    }

    case 'DELETE_EXPENSE': {
      const exp = state.expenses.find(e => e.id === payload);
      if (!exp) return state;
      const expAccountName = exp.paidFrom === 'Cash'
        ? 'Cash'
        : (exp.bankAccount || resolvePaymentAccount(state.accounts, 'Bank') || 'HDFC Bank - Current');
      const exTds = Number(exp.tdsAmount) || 0;
      const exNet = exp.amount - exTds;
      let accounts = updateAccountBalance(state.accounts, expAccountName, exNet); // refund the net paid
      if (exTds > 0) accounts = updateAccountBalance(accounts, exp.tdsAccount || TDS_PAYABLE_ACCOUNT, exTds); // reverse the Cr liability
      return { ...state, accounts, expenses: state.expenses.filter(e => e.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== exp.expenseNo) };
    }

    case 'ADD_CONTRA_ENTRY': {
      const counter = state.counters.contra + 1;
      const entry = { id: uuidv4(), contraNo: docNo(state, 'contra', 'contraEntries', payload.date, 'CTR', counter), financialYear: fyOf(state, payload.date), ...payload };
      let accounts = [...state.accounts];
      accounts = updateAccountBalance(accounts, entry.fromAccount, -entry.amount);
      accounts = updateAccountBalance(accounts, entry.toAccount, entry.amount);
      let ledger = addLedgerEntry(state, {
        type: 'Contra',
        refNo: entry.contraNo,
        account: `${entry.fromAccount} -> ${entry.toAccount}`,
        debit: entry.amount,
        credit: entry.amount,
        amount: entry.amount,
        narration: entry.narration || `Transfer ${entry.fromAccount} to ${entry.toAccount}`,
      });
      return {
        ...state,
        accounts,
        contraEntries: [...state.contraEntries, entry],
        ledgerEntries: ledger,
        counters: { ...state.counters, contra: counter },
      };
    }

    case 'DELETE_CONTRA_ENTRY': {
      const ctr = state.contraEntries.find(c => c.id === payload);
      if (!ctr) return state;
      let accounts = [...state.accounts];
      accounts = updateAccountBalance(accounts, ctr.fromAccount, ctr.amount);
      accounts = updateAccountBalance(accounts, ctr.toAccount, -ctr.amount);
      return { ...state, accounts, contraEntries: state.contraEntries.filter(c => c.id !== payload), ledgerEntries: state.ledgerEntries.filter(l => l.refNo !== ctr.contraNo) };
    }

    case 'ADD_STOCK_JOURNAL': {
      const counter = state.counters.stockJournal + 1;
      const entry = { id: uuidv4(), journalNo: docNo(state, 'stockJournal', 'stockJournals', payload.date, 'SJ', counter), financialYear: fyOf(state, payload.date), ...payload };
      let items = [...state.items];
      if (entry.itemId && entry.qty) {
        const qtyChange = entry.type === 'Add' ? Number(entry.qty) : -(Number(entry.qty) || 0);
        items = updateItemStock(items, entry.itemId, qtyChange);
      }
      return {
        ...state,
        items,
        stockJournals: [...state.stockJournals, entry],
        counters: { ...state.counters, stockJournal: counter },
      };
    }

    case 'DELETE_STOCK_JOURNAL': {
      const sj = state.stockJournals.find(s => s.id === payload);
      if (!sj) return state;
      const qtyReverse = sj.type === 'Add' ? -(Number(sj.qty) || 0) : (Number(sj.qty) || 0);
      let items = updateItemStock(state.items, sj.itemId, qtyReverse);
      return { ...state, items, stockJournals: state.stockJournals.filter(s => s.id !== payload) };
    }

    // ── Production: consume input (raw) items, produce output (finished) items ──
    // Inventory effect only: inputs decrease stock, outputs increase stock.
    case 'ADD_PRODUCTION': {
      const counter = state.counters.production + 1;
      const entry = {
        id: uuidv4(),
        productionNo: docNo(state, 'production', 'productions', payload.date, 'PRD', counter),
        financialYear: fyOf(state, payload.date),
        ...payload,
        inputs: payload.inputs || [],
        outputs: payload.outputs || [],
      };
      let items = [...state.items];
      entry.inputs.forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      entry.outputs.forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      return {
        ...state,
        items,
        productions: [...state.productions, entry],
        counters: { ...state.counters, production: counter },
      };
    }

    case 'UPDATE_PRODUCTION': {
      const old = state.productions.find(p => p.id === payload.id);
      if (!old) return state;
      let items = [...state.items];
      // Reverse the previous entry's stock effect…
      (old.inputs || []).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      (old.outputs || []).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      // …then apply the new one.
      const updated = { ...old, ...payload, inputs: payload.inputs || [], outputs: payload.outputs || [] };
      (updated.inputs).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      (updated.outputs).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      return { ...state, items, productions: state.productions.map(p => p.id === payload.id ? updated : p) };
    }

    case 'DELETE_PRODUCTION': {
      const prod = state.productions.find(p => p.id === payload);
      if (!prod) return state;
      let items = [...state.items];
      (prod.inputs || []).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      (prod.outputs || []).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      return { ...state, items, productions: state.productions.filter(p => p.id !== payload) };
    }

    // ── Stock Issue to Consumption: issue purchase items to a consumption head ──
    // Inventory effect only: issued items decrease stock.
    case 'ADD_STOCK_ISSUE': {
      const counter = state.counters.stockIssue + 1;
      const entry = {
        id: uuidv4(),
        issueNo: docNo(state, 'stockIssue', 'stockIssues', payload.date, 'SI', counter),
        financialYear: fyOf(state, payload.date),
        ...payload,
        items: payload.items || [],
      };
      let items = [...state.items];
      entry.items.forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      return {
        ...state,
        items,
        stockIssues: [...state.stockIssues, entry],
        counters: { ...state.counters, stockIssue: counter },
      };
    }

    case 'UPDATE_STOCK_ISSUE': {
      const old = state.stockIssues.find(s => s.id === payload.id);
      if (!old) return state;
      let items = [...state.items];
      // Reverse the previous issue (add stock back)…
      (old.items || []).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      // …then apply the new one.
      const updated = { ...old, ...payload, items: payload.items || [] };
      updated.items.forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, -(Number(li.qty) || 0)); });
      return { ...state, items, stockIssues: state.stockIssues.map(s => s.id === payload.id ? updated : s) };
    }

    case 'DELETE_STOCK_ISSUE': {
      const si = state.stockIssues.find(s => s.id === payload);
      if (!si) return state;
      let items = [...state.items];
      (si.items || []).forEach(li => { if (li.itemId && li.qty) items = updateItemStock(items, li.itemId, Number(li.qty) || 0); });
      return { ...state, items, stockIssues: state.stockIssues.filter(s => s.id !== payload) };
    }

    // ── Financial Year master ──
    case 'ADD_FINANCIAL_YEAR':
      return { ...state, financialYears: [...state.financialYears, { id: uuidv4(), ...payload }] };
    case 'UPDATE_FINANCIAL_YEAR':
      return { ...state, financialYears: state.financialYears.map(f => f.id === payload.id ? { ...f, ...payload } : f) };
    case 'DELETE_FINANCIAL_YEAR':
      return { ...state, financialYears: state.financialYears.filter(f => f.id !== payload) };

    // ── Prefix Setting master (per-transaction document numbering) ──
    case 'ADD_PREFIX_SETTING':
      return { ...state, prefixSettings: [...state.prefixSettings, { id: uuidv4(), ...payload }] };
    case 'UPDATE_PREFIX_SETTING':
      return { ...state, prefixSettings: state.prefixSettings.map(p => p.id === payload.id ? { ...p, ...payload } : p) };
    case 'DELETE_PREFIX_SETTING':
      return { ...state, prefixSettings: state.prefixSettings.filter(p => p.id !== payload) };

    // ── Account Group master ──
    case 'ADD_ACCOUNT_GROUP':
      return { ...state, accountGroups: [...state.accountGroups, { id: uuidv4(), ...payload }] };
    case 'UPDATE_ACCOUNT_GROUP':
      return { ...state, accountGroups: state.accountGroups.map(g => g.id === payload.id ? { ...g, ...payload } : g) };
    case 'DELETE_ACCOUNT_GROUP':
      return { ...state, accountGroups: state.accountGroups.filter(g => g.id !== payload) };

    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const prevStateRef = useRef(null);
  const saveTimerRef = useRef(null);
  // In edge mode the DB is locked, so app data can only be fetched once the
  // user is authenticated (the Edge Function authorizes reads via the token).
  const { isLoggedIn, authLoading } = useAuth();

  useEffect(() => {
    // Edge mode: data can only be fetched after login. While we wait, clear the
    // loading flag so App.js shows the login page instead of the loading screen
    // forever. (Direct mode loads immediately on mount.)
    if (isEdgeMode()) {
      if (authLoading) return;                       // session check still running
      if (!isLoggedIn) { setLoading(false); return; } // not logged in → show login
    }
    if (initialized) return;
    async function init() {
      setLoading(true);
      let loadedState = null;

      if (isSupabaseConfigured()) {
        loadedState = await loadAppData(defaultState);
        if (!loadedState) {
          await saveAppData(defaultState, null);
        }
      } else {
        try {
          const saved = localStorage.getItem(storageKey());
          if (saved) loadedState = JSON.parse(saved);
        } catch (e) {
          console.error('Failed to load from localStorage:', e);
        }
      }

      if (loadedState) {
        dispatch({ type: 'LOAD_STATE', payload: loadedState });
      }

      setLoading(false);
      setInitialized(true);
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, authLoading]);

  useEffect(() => {
    if (!initialized) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      if (isSupabaseConfigured()) {
        saveAppData(state, prevStateRef.current);
      } else {
        try {
          localStorage.setItem(storageKey(), JSON.stringify(state));
        } catch (e) {
          console.error('Failed to save to localStorage:', e);
        }
      }
      prevStateRef.current = state;
    }, isSupabaseConfigured() ? 500 : 0);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [state, initialized]);

  const resetData = useCallback(async () => {
    dispatch({ type: 'RESET_DATA' });
    if (isSupabaseConfigured()) {
      await clearAppData(defaultState);
    } else {
      localStorage.removeItem(storageKey());
    }
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch, loading, resetData }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
